import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ScheduleEditModal from "components/ScheduleEditModal";
import SchedulesPanel from "components/device/SchedulesPanel";
import { useUpdateSchedules } from "hooks/useDevices";
import { notify } from "utils/notify";
import { Schedule } from "types/device.types";
import {
  ApiSchedule,
  apiSchedulesToShadowSchedules,
  convertToApiSchedule,
  formatTime,
  normalizeDaysBits,
  timeToSeconds,
} from "./scheduleUtils";

interface ScheduleSectionProps {
  serialNumber: string;
  schedules: Schedule[];
  firmwareVersion: string;
}

export function ScheduleSection({
  serialNumber,
  schedules,
  firmwareVersion,
}: ScheduleSectionProps) {
  const queryClient = useQueryClient();
  const updateSchedules = useUpdateSchedules();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<Schedule | null>(null);
  const [currentScheduleIndex, setCurrentScheduleIndex] = useState(-1);
  const [updatingSchedules, setUpdatingSchedules] = useState(false);
  const modalHistoryRef = useRef(false);

  const clearEditModal = useCallback(() => {
    setEditModalVisible(false);
    setCurrentSchedule(null);
    setCurrentScheduleIndex(-1);
  }, []);

  const closeEditModal = useCallback(() => {
    if (modalHistoryRef.current) {
      modalHistoryRef.current = false;
      window.history.back();
      return;
    }

    clearEditModal();
  }, [clearEditModal]);

  useEffect(() => {
    const handlePopState = () => {
      if (!modalHistoryRef.current) return;

      modalHistoryRef.current = false;
      clearEditModal();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [clearEditModal]);

  const handleEditSchedule = (schedule: Schedule, index: number) => {
    if (!modalHistoryRef.current) {
      window.history.pushState(
        { ...window.history.state, scheduleEditor: true },
        "",
        window.location.href,
      );
      modalHistoryRef.current = true;
    }

    setCurrentSchedule(schedule);
    setCurrentScheduleIndex(index);
    setEditModalVisible(true);
  };

  const handleSaveSchedule = (
    updatedSchedule: {
      on: string;
      off: string;
      days: string;
      speed: string | number;
    },
    index: number,
  ) => {
    setUpdatingSchedules(true);

    if (!schedules) {
      notify.error("Schedule data not available");
      setUpdatingSchedules(false);
      closeEditModal();
      return;
    }

    const convertedSchedule: ApiSchedule = {
      on: timeToSeconds(updatedSchedule.on),
      off: timeToSeconds(updatedSchedule.off),
      days: normalizeDaysBits(updatedSchedule.days),
      speed:
        typeof updatedSchedule.speed === "string"
          ? parseInt(updatedSchedule.speed, 10)
          : updatedSchedule.speed,
    };

    const apiSchedules: ApiSchedule[] = schedules.map((schedule, i) => {
      if (i === index) return convertedSchedule;
      return convertToApiSchedule(schedule);
    });

    updateSchedules.mutate(
      {
        serialNumber,
        firmwareVersion,
        schedules: apiSchedules,
      },
      {
        onSuccess: () => {
          const nextSchedules = apiSchedulesToShadowSchedules(apiSchedules);
          queryClient.setQueryData(
            ["deviceStatus", serialNumber],
            (oldData: any) => {
              if (!oldData?.response?.shadow) return oldData;
              return {
                ...oldData,
                response: {
                  ...oldData.response,
                  shadow: {
                    ...oldData.response.shadow,
                    schedules: nextSchedules,
                  },
                },
              };
            },
          );
          queryClient.invalidateQueries({
            queryKey: ["deviceStatus", serialNumber],
          });
          notify.success("Schedule updated successfully");
        },
        onError: (error: any) => {
          console.error("Failed to update schedules:", error);
          notify.error(
            error?.response?.data?.message || "Failed to update schedules",
          );
        },
        onSettled: () => {
          setUpdatingSchedules(false);
          closeEditModal();
        },
      },
    );
  };

  return (
    <>
      <SchedulesPanel
        schedules={schedules}
        formatTime={formatTime}
        handleEditSchedule={handleEditSchedule}
        isLoading={updatingSchedules}
      />

      {currentSchedule && (
        <ScheduleEditModal
          visible={editModalVisible}
          onHide={closeEditModal}
          schedule={{
            on: String(currentSchedule.on),
            off: String(currentSchedule.off),
            days: normalizeDaysBits(currentSchedule.days),
            speed: currentSchedule.speed,
          }}
          scheduleIndex={currentScheduleIndex}
          onSave={handleSaveSchedule}
        />
      )}
    </>
  );
}
